/**
 * Which window the Meeting Companion lives in (ported from Relay's Interview Console,
 * RELAY-718/720, where it was proven on recruiters' calls).
 *
 *  - `pip`    Document Picture-in-Picture: a real always-on-top window, so the companion stays
 *             visible over a full-screen Meet/Zoom call. Chromium 116+ only.
 *  - `popup`  A `window.open` popup: not always-on-top, but a separate window to park on a
 *             second monitor. The fallback where Document PiP is missing (Firefox, Safari).
 *  - `sheet`  An in-page panel. No second window: the escape hatch for a locked-down browser.
 *
 * The override (`crm:meeting-companion:surface` in localStorage) is a manual escape hatch for
 * testing and for a browser that mis-detects; without one the best supported surface is used.
 */

export type Surface = 'pip' | 'popup' | 'sheet';

/** A narrow column beside a call: notes and action items fit; the call keeps the screen. */
export const DEFAULT_WINDOW_WIDTH = 440;
export const DEFAULT_WINDOW_HEIGHT = 780;

export const SURFACE_OVERRIDE_KEY = 'crm:meeting-companion:surface';
export const SIZE_KEY = 'crm:meeting-companion:size';

const SURFACES: readonly Surface[] = ['pip', 'popup', 'sheet'];

const isSurface = (value: unknown): value is Surface =>
  typeof value === 'string' && (SURFACES as readonly string[]).includes(value);

export function supportsDocumentPip(): boolean {
  return typeof window.documentPictureInPicture?.requestWindow === 'function';
}

export function readSurfaceOverride(): Surface | null {
  try {
    const raw = window.localStorage.getItem(SURFACE_OVERRIDE_KEY);
    return isSurface(raw) ? raw : null;
  } catch (err) {
    console.warn('meeting companion: surface override read failed, auto-detecting', err);
    return null;
  }
}

export function writeSurfaceOverride(surface: Surface | null): void {
  try {
    if (surface === null) window.localStorage.removeItem(SURFACE_OVERRIDE_KEY);
    else window.localStorage.setItem(SURFACE_OVERRIDE_KEY, surface);
  } catch (err) {
    console.warn('meeting companion: surface override write failed', err);
  }
}

/** The override when set, else `pip` where supported and `popup` elsewhere; never `sheet` by itself. */
export function resolveSurface(): Surface {
  return readSurfaceOverride() ?? (supportsDocumentPip() ? 'pip' : 'popup');
}

export function surfaceLabel(surface: Surface): string {
  if (surface === 'pip') return 'Floating';
  if (surface === 'popup') return 'Window';
  return 'Panel';
}

/** The last external-window size, clamped so a corrupt value never opens an unusable window. */
export function readStoredSize(): { width: number; height: number } {
  try {
    const raw = window.localStorage.getItem(SIZE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as { width?: unknown; height?: unknown };
      const width = typeof p.width === 'number' ? p.width : DEFAULT_WINDOW_WIDTH;
      const height = typeof p.height === 'number' ? p.height : DEFAULT_WINDOW_HEIGHT;
      if (width >= 320 && width <= 2000 && height >= 320 && height <= 2000)
        return { width, height };
    }
  } catch (err) {
    console.warn('meeting companion: window size read failed, using the default', err);
  }
  return { width: DEFAULT_WINDOW_WIDTH, height: DEFAULT_WINDOW_HEIGHT };
}

export function writeStoredSize(width: number, height: number): void {
  try {
    window.localStorage.setItem(SIZE_KEY, JSON.stringify({ width, height }));
  } catch (err) {
    console.warn('meeting companion: window size write failed', err);
  }
}
